
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionStatus } from './types';
import { lastFetchTimes, pendingRequests, subscriptionCache, cacheSubscriptionStatus, clearCache } from './cache';

/**
 * Get subscription status for a user
 */
export async function getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
  try {
    console.log('Fetching subscription status for user:', userId);
    
    // First check if there's already a confirmed active subscription in localStorage
    const confirmedActive = localStorage.getItem('subscription_confirmed_active') === 'true';
    if (confirmedActive) {
      console.log('Using confirmed active subscription from localStorage');
      // Return a guaranteed active subscription status - this prevents any UI flicker
      const cachedActiveStatus: SubscriptionStatus = {
        remainingFreeTransforms: 0,
        subscriptionType: 'monthly', // Default to monthly
        isActive: true
      };
      
      // Update the cache with this status
      cacheSubscriptionStatus(userId, cachedActiveStatus);
      
      return cachedActiveStatus;
    }
    
    // Implement simple debounce to prevent multiple rapid calls for the same user
    const now = Date.now();
    const lastFetch = lastFetchTimes[userId] || 0;
    
    // Check if we have a recent cache entry (less than 10 seconds old)
    const cacheEntry = subscriptionCache[userId];
    if (cacheEntry && now - cacheEntry.timestamp < 10000) {
      console.log('Using cached subscription status, age:', (now - cacheEntry.timestamp)/1000, 'seconds');
      return cacheEntry.status;
    }
    
    // If there's already a request in progress for this user, return that instead of starting a new one
    if (pendingRequests[userId]) {
      console.log('Request already in progress, using existing promise');
      return pendingRequests[userId];
    }
    
    if (now - lastFetch < 2000) { // 2 second minimum between fetches
      console.log('Debouncing subscription check, last check was too recent');
      // Return from localStorage cache if we have it
      const cachedStatus = localStorage.getItem(`subscription_status_${userId}`);
      if (cachedStatus) {
        console.log('Returning localStorage cached subscription status');
        return JSON.parse(cachedStatus);
      }
    }
    
    // Update last fetch time
    lastFetchTimes[userId] = now;
    
    // Create the promise for the fetch operation
    const fetchPromise = (async () => {
      try {
        // Check for subscription_activated flag to ensure webhook processing
        const justActivated = localStorage.getItem('subscription_activated');
        if (justActivated) {
          console.log('Detected subscription activation flag, adding delay before fetching status');
          // Add a significant delay to ensure Stripe webhook has processed
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
        
        // First try to get the user's subscription
        let { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', userId)
          .single();

        // If no subscription found, create one with default values
        if (error && error.code === 'PGRST116') {
          console.log('No subscription found for user, creating default entry');
          
          // Insert a new default subscription
          const { data: insertData, error: insertError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: userId,
              subscription_type: 'none',
              remaining_free_transforms: 3
            })
            .select()
            .single();
            
          if (insertError) {
            console.error('Error creating default subscription:', insertError);
            throw insertError;
          }
          
          data = insertData;
        } else if (error) {
          console.error('Error fetching subscription data:', error);
          throw error;
        }
        
        console.log('Raw subscription data from database:', data);

        // A subscription is active if the type is not 'none' AND:
        // 1. subscription_type is 'monthly' or 'yearly'
        // 2. There is no end date (permanent subscription)
        // 3. The end date is in the future
        const hasValidType = data.subscription_type === 'monthly' || data.subscription_type === 'yearly';
        const hasValidEndDate = data.subscription_end === null || new Date(data.subscription_end) > new Date();
        const isActive = hasValidType && hasValidEndDate;
        
        console.log('Subscription status details:', { 
          type: data.subscription_type,
          end: data.subscription_end,
          isActive,
          hasValidType,
          hasValidEndDate,
          remainingFree: data.remaining_free_transforms
        });

        const status = {
          remainingFreeTransforms: data.remaining_free_transforms || 0,
          subscriptionType: data.subscription_type || 'none',
          isActive: isActive
        };
        
        // Store in cache and handle active subscription flags
        cacheSubscriptionStatus(userId, status);
        
        // If subscription is NOT active, clear any incorrect "active" flag
        if (!status.isActive) {
          localStorage.removeItem('subscription_confirmed_active');
          // Additionally, for new users, ensure we're completely clear
          if (status.subscriptionType === 'none') {
            window.dispatchEvent(new Event('subscription_updated'));
          }
        }
        
        return status;
      } finally {
        // Remove this promise from pending requests when it completes
        delete pendingRequests[userId];
      }
    })();
    
    // Store the promise
    pendingRequests[userId] = fetchPromise;
    
    return fetchPromise;
  } catch (error) {
    console.error('Error fetching subscription status:', error);
    // Return default values if there's an error
    return {
      remainingFreeTransforms: 0,
      subscriptionType: 'none',
      isActive: false
    };
  }
}

/**
 * Decrements the free transforms count for a user
 */
export async function decrementFreeTransforms(userId: string): Promise<number> {
  try {
    console.log('Attempting to decrement free transforms for user:', userId);
    
    // First check if the user has a subscription record
    const { data: checkData, error: checkError } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    // If no record exists, create one first
    if (checkError && checkError.code === 'PGRST116') {
      console.log('No subscription record found, creating one first');
      
      // Create a default subscription with 3 free transforms
      const { error: insertError } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: userId,
          subscription_type: 'none',
          remaining_free_transforms: 3
        });
        
      if (insertError) {
        console.error('Error creating subscription record:', insertError);
        return 0;
      }
    } else if (checkError) {
      console.error('Error checking subscription record:', checkError);
      return 0;
    }
    
    // Now decrement the free transforms
    const { data, error } = await supabase
      .rpc('decrement_free_transforms', { user_id: userId });

    if (error) {
      console.error('Error decrementing free transforms:', error);
      throw error;
    }
    
    // Clear cache when we decrement to ensure fresh data
    clearCache(userId);
    
    return data || 0;
  } catch (error) {
    console.error('Error decrementing free transforms:', error);
    return 0;
  }
}
